import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import { useTranslation } from 'react-i18next'
import { ARUBA_CENTER, DEFAULT_ZOOM } from '../lib/constants'
import { demoSightings } from '../data/demoLost'

const darkMapStyle = [
  { elementType: 'geometry', stylers: [{ color: '#0d141c' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0d141c' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8fa3b8' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1a2836' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#243041' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#071018' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
]

interface Props {
  focusAnimalId: number | null
  activeSightingIndex: number
  onMarkerClick: (animalId: number, sightingId: number) => void
}

function MapFallback({
  focus,
}: {
  focus?: { id: number; lat: number; lng: number }
}) {
  const { t } = useTranslation()
  return (
    <div className="map-fill map-fallback">
      <div>
        <strong>ARUBA GRID</strong>
        {t('status.mapLoading')}
        <p style={{ marginTop: '0.75rem', maxWidth: 360 }}>
          Set <code>VITE_GOOGLE_MAPS_API_KEY</code> in <code>.env</code> to activate Google Maps.
          Demo pins: {demoSightings.map((s) => `#${s.id}`).join(', ')}
        </p>
        {focus && (
          <p style={{ color: 'var(--amber)' }}>
            Focus → sighting #{focus.id} @ {focus.lat.toFixed(4)}, {focus.lng.toFixed(4)}
          </p>
        )}
      </div>
    </div>
  )
}

function GoogleRadarMap({ focusAnimalId, activeSightingIndex, onMarkerClick }: Props) {
  const { t } = useTranslation()
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string
  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: apiKey })

  const animalSightings = focusAnimalId
    ? demoSightings.filter((s) => s.animal_id === focusAnimalId)
    : demoSightings
  const focus = animalSightings[activeSightingIndex] ?? animalSightings[0]

  if (loadError) return <MapFallback focus={focus} />
  if (!isLoaded) return <div className="map-fill map-fallback">{t('status.mapLoading')}</div>

  return (
    <div className="map-fill">
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={focus ? { lat: focus.lat, lng: focus.lng } : ARUBA_CENTER}
        zoom={DEFAULT_ZOOM}
        options={{
          styles: darkMapStyle,
          disableDefaultUI: true,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          backgroundColor: '#0d141c',
        }}
      >
        {animalSightings.map((s, idx) => (
          <Marker
            key={s.id}
            position={{ lat: s.lat, lng: s.lng }}
            opacity={focusAnimalId && idx !== activeSightingIndex ? 0.55 : 1}
            onClick={() => onMarkerClick(s.animal_id, s.id)}
            title={`#${s.id}`}
          />
        ))}
      </GoogleMap>
    </div>
  )
}

export function RadarMap(props: Props) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined
  const animalSightings = props.focusAnimalId
    ? demoSightings.filter((s) => s.animal_id === props.focusAnimalId)
    : demoSightings
  const focus = animalSightings[props.activeSightingIndex] ?? animalSightings[0]

  if (!apiKey) return <MapFallback focus={focus} />
  return <GoogleRadarMap {...props} />
}
